"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import MenuItem from "@mui/material/MenuItem";

import {
  createBusDriver,
  updateBusDriver,
  deleteBusDriver,
  createBulkBusDriver,
  deleteAllBusDriver,
  uploadDriverImage, // ← New import
} from "../../api/busDrivers";

import customFetch from "../../hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";
import imageCompression from "browser-image-compression";

const DriversTab = () => {
  const [rows, setRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    DriverID: null,
    DriverName: "",
    EmployeeCode: "",
    ContactNumber: "",
    Email: "",
    Status: "Active",
  });

  // Image Upload Modal States
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedDriverForImage, setSelectedDriverForImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // View Image Modal States
  const [openViewImageModal, setOpenViewImageModal] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState("");

  // Excel Menu
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  // Delete States
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canConfirm, setCanConfirm] = useState(false);
  const countdownRef = useRef(null);

  const imageFileInputRef = useRef(null);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (openDeleteAllDialog && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanConfirm(true);
      clearTimeout(countdownRef.current);
    }
    return () => clearTimeout(countdownRef.current);
  }, [openDeleteAllDialog, countdown]);

  const fetchDrivers = async () => {
    try {
      const data = await customFetch("getDriverDetails/0");
      setRows(data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({
      DriverID: null,
      DriverName: "",
      EmployeeCode: "",
      ContactNumber: "",
      Email: "",
      Status: "Active",
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (driver) => {
    setIsEditMode(true);
    setFormData({
      DriverID: driver.DriverID,
      DriverName: driver.DriverName || "",
      EmployeeCode: driver.EmployeeCode || "",
      ContactNumber: driver.ContactNumber || "",
      Email: driver.Email || "",
      Status: driver.Status || "Active",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const baseUrl = process.env.REACT_APP_BASE_API_URL || "http://localhost:8000/api/";

  const handleSubmit = async () => {
    if (!formData.DriverName.trim() || !formData.EmployeeCode.trim()) {
      alert("Driver Name and Employee Code are required");
      return;
    }

    const payload = {
      DriverName: formData.DriverName,
      EmployeeCode: formData.EmployeeCode,
      ContactNumber: formData.ContactNumber || "",
      Email: formData.Email || "",
      Status: formData.Status,
    };

    try {
      let response;
      if (isEditMode) {
        payload.DriverID = formData.DriverID;
        response = await updateBusDriver(payload);
        if (response.status) {
          setRows((prev) =>
            prev.map((row) => (row.DriverID === formData.DriverID ? { ...row, ...payload } : row))
          );
          alert("Driver updated successfully!");
          handleCloseModal();
        }
      } else {
        response = await createBusDriver(payload);
        if (response.status) {
          fetchDrivers();
          alert("Driver added successfully!");
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error("Error saving driver:", error);
      alert("Failed to save driver");
    }
  };

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBusDriver(deleteId);
      setRows((prev) => prev.filter((row) => row.DriverID !== deleteId));
      alert("Driver deleted successfully!");
    } catch (error) {
      console.error("Error deleting driver:", error);
    }
    setOpenConfirmDialog(false);
  };

  const handleCloseDeleteAll = () => {
    setOpenDeleteAllDialog(false);
    setCountdown(5);
    setCanConfirm(false);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      const response = await deleteAllBusDriver();
      if (response.status) {
        setRows([]);
        alert("All drivers deleted successfully!");
      }
    } catch (error) {
      console.error("Delete all failed:", error);
    } finally {
      handleCloseDeleteAll();
    }
  };

  // ==================== IMAGE UPLOAD ====================
  const handleOpenImageUpload = (driver) => {
    setSelectedDriverForImage(driver);
    setPreviewImage(null);
    setSelectedFile(null);
    setOpenImageModal(true);
  };

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const compressAndUploadImage = async () => {
    if (!selectedFile || !selectedDriverForImage?.EmployeeCode) return;

    setUploading(true);

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
        initialQuality: 0.9,
      };

      const compressedFile = await imageCompression(selectedFile, options);

      const renamedFile = new File([compressedFile], `${selectedDriverForImage.EmployeeCode}.jpg`, {
        type: "image/jpeg",
      });

      await uploadDriverImage(renamedFile, selectedDriverForImage.EmployeeCode);

      setOpenImageModal(false);
      setPreviewImage(null);
      setSelectedFile(null);
      fetchDrivers(); // Refresh to show updated data if needed
    } catch (error) {
      console.error("Image upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  // View Image when clicking Driver Name
  const handleNameClick = (driver) => {
    const imageUrl = `${baseUrl}uploads/${driver.EmployeeCode}.jpg`;
    setViewImageUrl(imageUrl);
    setOpenViewImageModal(true);
  };

  // Excel Handlers
  const handleExcelButtonClick = (event) => {
    setExcelMenuAnchorEl(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchorEl(null);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [["DriverName", "EmployeeCode", "ContactNumber", "Email", "Status"]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Drivers");
    const fileName = `drivers_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    handleExcelMenuClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
    handleExcelMenuClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const data = await readExcelFile(file);
      if (data?.length > 0) {
        await createBulkBusDriver(data);
        fetchDrivers();
      }
    } catch (error) {
      console.error("Excel processing error:", error);
    }
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validData = jsonData
            .filter((item) => item.DriverName && item.EmployeeCode)
            .map((item) => ({
              DriverName: item.DriverName,
              EmployeeCode: String(item.EmployeeCode),
              ContactNumber: String(item.ContactNumber || ""),
              Email: item.Email || "",
              Status: item.Status || "Active",
            }));

          resolve(validData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Columns
  const columns = [
    {
      field: "DriverName",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            cursor: "pointer",
            "&:hover": { textDecoration: "underline", color: "primary.main" },
          }}
          onClick={() => handleNameClick(params.row)}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: "EmployeeCode", headerName: "Employee Code", flex: 1, minWidth: 150 },
    { field: "ContactNumber", headerName: "Contact Number", flex: 1, minWidth: 150 },
    { field: "Email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "Status", headerName: "Status", flex: 1, minWidth: 100 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 130,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<CameraAltIcon color="primary" />}
          label="Upload Photo"
          onClick={() => handleOpenImageUpload(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeleteAllDialog(true);
            setCountdown(5);
            setCanConfirm(false);
          }}
        >
          Delete All Drivers
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleExcelButtonClick}
          startIcon={<BackupTableIcon />}
        >
          Excel
        </Button>

        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={handleExcelMenuClose}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
        </Menu>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />

        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Driver
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.DriverID}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Add/Edit Driver Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Driver" : "Add New Driver"}</h2>

          <TextField
            margin="dense"
            label="Driver Name"
            fullWidth
            value={formData.DriverName}
            onChange={(e) => setFormData({ ...formData, DriverName: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Employee Code"
            fullWidth
            value={formData.EmployeeCode}
            onChange={(e) => setFormData({ ...formData, EmployeeCode: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Contact Number"
            fullWidth
            value={formData.ContactNumber}
            onChange={(e) => setFormData({ ...formData, ContactNumber: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={formData.Email}
            onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.Status}
              label="Status"
              onChange={(e) => setFormData({ ...formData, Status: e.target.value })}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ==================== IMAGE UPLOAD MODAL ==================== */}
      <Modal open={openImageModal} onClose={() => setOpenImageModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Upload Photo - {selectedDriverForImage?.DriverName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Image will be saved as <strong>{selectedDriverForImage?.EmployeeCode}.jpg</strong>
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => imageFileInputRef.current?.click()}
            sx={{
              border: "2px dashed #1976d2",
              borderRadius: 2,
              p: 6,
              textAlign: "center",
              cursor: "pointer",
              minHeight: 250,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              backgroundColor: "#f8fafc",
              "&:hover": { backgroundColor: "#f0f7ff" },
            }}
          >
            {previewImage ? (
              <img
                src={previewImage}
                alt="preview"
                style={{ maxWidth: "100%", maxHeight: "280px", borderRadius: 8 }}
              />
            ) : (
              <>
                <CameraAltIcon sx={{ fontSize: 60, color: "#1976d2", mb: 2 }} />
                <Typography>Drag & drop image here</Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to select file
                </Typography>
              </>
            )}
          </Box>

          {/* Separate Hidden Input for Images */}
          <input
            type="file"
            ref={imageFileInputRef} // ← New ref
            accept="image/*" // ← Important
            style={{ display: "none" }}
            onChange={(e) => handleFileSelect(e.target.files[0])}
          />

          <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenImageModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={compressAndUploadImage}
              disabled={!selectedFile || uploading}
              startIcon={<FileUploadIcon />}
            >
              {uploading ? "Uploading..." : "Upload Photo"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* View Image Modal */}
      <Modal open={openViewImageModal} onClose={() => setOpenViewImageModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            maxWidth: "90%",
            maxHeight: "90vh",
          }}
        >
          <img
            src={viewImageUrl}
            alt="Driver"
            style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: 8 }}
            onError={() => {
              alert("No photo found for this driver");
              setOpenViewImageModal(false);
            }}
          />
        </Box>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this driver?"
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />

      {/* Delete All Modal */}
      <Modal open={openDeleteAllDialog} onClose={handleCloseDeleteAll}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error">
            Delete ALL Drivers?
          </Typography>
          <Typography sx={{ my: 3 }}>This action cannot be undone.</Typography>
          <Typography variant="h3" fontWeight="bold" sx={{ my: 3 }}>
            {countdown}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button onClick={handleCloseDeleteAll}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAll}
              disabled={!canConfirm}
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DriversTab;
